/**
 * <silo-pay-toggle> — PDP "Select your payment option" (Pay in 12 / Pay in full).
 *
 * Monthly mode rewrites every price on the PDP to its N-payment figure
 * (N = data-payments, default 12):
 *   - main device price + compare
 *   - every bundle row price (+$198 → +$16.50/mo) with an alternate-figure hint
 *   - the Bundle Summary total + its sub line
 * "Pay in full" restores the exact original strings.
 *
 * Architecture / invariants:
 *   - SSOT: each element's original text lives in its own data-sp-once /
 *     data-sp-once-html; the derived monthly string in data-sp-mo. No state
 *     is duplicated outside the DOM the value belongs to.
 *   - Reentrancy: all writes are diffed (no-op at steady state) and happen
 *     under _writing; the MutationObserver never reacts to our own writes.
 *     External writes arriving inside that window set _dirty and get exactly
 *     one follow-up sync — diffed writes guarantee convergence (no loops).
 *   - Performance: mutations from the sale-countdown ticker (1/s inside the
 *     same subtree) are filtered out before syncing.
 *   - Lifecycle: listener, observer, and the pending flag-clear timer are all
 *     torn down in disconnectedCallback; no document/window listeners at all.
 */
(() => {
  'use strict';
  if (customElements.get('silo-pay-toggle')) return;

  const num = (t) => parseFloat(String(t).replace(/[^0-9.]/g, '')) || 0;
  const money = (n) => {
    const r = Math.round(n * 100) / 100;
    const s = Number.isInteger(r) ? String(r) : r.toFixed(2);
    return '$' + s;
  };
  /** Countdown ticks mutate text every second — never a price change. */
  const IGNORE = '.sale-countdown, .sale-countdown-inline';
  const isNoise = (record) => {
    const el = record.target.nodeType === 1 ? record.target : record.target.parentElement;
    return !!(el && el.closest && el.closest(IGNORE));
  };

  class SiloPayToggle extends HTMLElement {
    connectedCallback() {
      this.payments = Number(this.dataset.payments) || 12;
      this.mode = 'once';
      this._writing = false;
      this._dirty = false;
      this._timer = 0;

      this._onClick = (e) => {
        const btn = e.target.closest('[data-sp-mode]');
        if (btn) this.setMode(btn.dataset.spMode);
      };
      this.addEventListener('click', this._onClick);

      // Radio-group keyboard pattern: arrows move + select.
      this._onKey = (e) => {
        const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
        if (!keys.includes(e.key) || !e.target.closest('[data-sp-mode]')) return;
        const btns = Array.from(this.querySelectorAll('[data-sp-mode]'));
        const idx = btns.findIndex((btn) => btn.classList.contains('is-selected'));
        const next = e.key === 'ArrowLeft' || e.key === 'ArrowUp'
          ? Math.max(0, idx - 1)
          : Math.min(btns.length - 1, idx + 1);
        if (next !== idx) {
          e.preventDefault();
          this.setMode(btns[next].dataset.spMode);
          btns[next].focus();
        }
      };
      this.addEventListener('keydown', this._onKey);

      this._root = document.querySelector('.product__info-wrapper') || document.body;
      this._mo = new MutationObserver((records) => {
        if (this._writing) { this._dirty = true; return; }
        if (records.every(isNoise)) return;
        this._sync();
      });
      this._mo.observe(this._root, { childList: true, subtree: true, characterData: true });
      this._sync();

      // AB-test hook: server prices always render as pay-in-full, so state
      // starts at 'once'; the desired default (URL param beats the section
      // setting) is applied through the normal setMode path. setMode also
      // repaints the buttons, covering a server-selected 'monthly' default.
      const want = (new URLSearchParams(location.search).get('sp_mode') || this.dataset.defaultMode || 'once').toLowerCase();
      if (want === 'monthly') this.setMode('monthly');
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKey);
      if (this._mo) { this._mo.disconnect(); this._mo = null; }
      if (this._timer) { clearTimeout(this._timer); this._timer = 0; }
      this._writing = false;
      this._dirty = false;
    }

    setMode(mode) {
      if (mode !== 'monthly' && mode !== 'once') return;
      if (mode === this.mode) return;
      this.mode = mode;
      this.querySelectorAll('[data-sp-mode]').forEach((b) => {
        const on = b.dataset.spMode === mode;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      this._sync();
    }

    _monthlyOf(base) {
      return money(base / this.payments) + '/mo';
    }

    _sync() {
      this._writing = true;
      try {
        this._prices();
        this._hints();
        this._summary();
        this._fromLine();
      } catch (e) { /* never break the PDP */ }
      // Our own mutation records flush as microtasks; this macrotask timer
      // clears the flag strictly after they were delivered (and skipped).
      // An external write observed meanwhile set _dirty — run one follow-up
      // sync for it. Diffed writes make that follow-up a no-op unless real.
      if (this._timer) clearTimeout(this._timer);
      this._timer = setTimeout(() => {
        this._timer = 0;
        this._writing = false;
        if (this._dirty) {
          this._dirty = false;
          this._sync();
        }
      }, 0);
    }

    /* Every price element: monthly ⇄ once, original string preserved.
       (.customCompare stays untouched — the struck figure keeps showing the
       full price in both modes, per design.) */
    _prices() {
      this._root.querySelectorAll('.customPrice').forEach((el) => {
        const cur = el.textContent.trim();
        if (this.mode === 'monthly') {
          if (cur && cur === el.dataset.spMo) return; // our own value — nothing to do
          if (cur && !cur.includes('/mo')) el.dataset.spOnce = cur; // external update → new base
          const base = num(el.dataset.spOnce);
          if (!base) return;
          const prefix = (el.dataset.spOnce || '').trim().startsWith('+') ? '+' : '';
          const txt = prefix + this._monthlyOf(base);
          el.dataset.spMo = txt;
          if (cur !== txt) el.textContent = txt;
        } else if (el.dataset.spOnce && cur.includes('/mo')) {
          el.textContent = el.dataset.spOnce;
        }
      });
    }

    /* Wrap a price element in a right-aligned column so its hint can sit
       directly beneath it. Idempotent; the element keeps its identity, so the
       variant/coupon engines' .customPrice selectors are unaffected. */
    _col(priceEl) {
      let col = priceEl.closest('.sp-price-col');
      if (!col) {
        col = document.createElement('span');
        col.className = 'sp-price-col';
        const row = document.createElement('span');
        row.className = 'sp-price-row';
        // The struck compare (when it precedes the price, as in the device
        // card) joins the price on the first line — otherwise the hint's
        // width would push them apart. Element identities are preserved, so
        // the variant/coupon engines' selectors keep working.
        const prev = priceEl.previousElementSibling;
        const compare = prev && prev.classList.contains('customCompare') ? prev : null;
        priceEl.replaceWith(col);
        col.appendChild(row);
        if (compare) row.appendChild(compare);
        row.appendChild(priceEl);
      }
      return col;
    }

    _setHint(col, txt) {
      let hint = col.querySelector('.sp-row-hint');
      if (!txt) { if (hint) hint.remove(); return; }
      if (!hint) {
        hint = document.createElement('span');
        hint.className = 'sp-row-hint';
        col.appendChild(hint);
      }
      if (hint.textContent !== txt) hint.textContent = txt;
    }

    /* Hints under each price: the alternate representation. */
    _hints() {
      // Bundle rows: "or +$16.50/mo" ⇄ "or +$198 one-time" (active row only, via CSS)
      this._root.querySelectorAll('.bundle-option').forEach((row) => {
        const priceEl = row.querySelector('.customPrice');
        if (!priceEl) return;
        const base = num(priceEl.dataset.spOnce || priceEl.textContent);
        const txt = !base ? '' : this.mode === 'monthly'
          ? 'or +' + money(base) + ' total'
          : 'or +' + this._monthlyOf(base);
        this._setHint(this._col(priceEl), txt);
      });
      // Device color card: the alternate figure under the price —
      // monthly mode: "$249 total"; pay-in-full mode: "or $20.75/mo".
      const dev = this._root.querySelector('div.bundle-options .customPrice');
      if (dev) {
        const once = (dev.dataset.spOnce || '').trim();
        let txt = '';
        if (this.mode === 'monthly') {
          if (once) txt = once + ' total';
        } else {
          const base = num(once || dev.textContent);
          if (base) txt = 'or ' + this._monthlyOf(base);
        }
        this._setHint(this._col(dev), txt);
      }
    }

    /* "From $399 or $33.25/mo" header line: flips with the toggle. */
    _fromLine() {
      const main = this._root.querySelector('[data-sp-from-price]');
      const alt = this._root.querySelector('[data-sp-from-alt]');
      if (!main || !alt) return;
      if (!main.dataset.spOnce) {
        main.dataset.spOnce = main.textContent.trim();
        alt.dataset.spOnce = alt.textContent.trim();
      }
      const base = num(main.dataset.spOnce);
      if (!base) return;
      const set = (el, txt) => { if (el.textContent !== txt) el.textContent = txt; };
      if (this.mode === 'monthly') {
        set(main, 'From ' + this._monthlyOf(base));
        set(alt, 'or ' + money(base));
      } else {
        set(main, main.dataset.spOnce);
        set(alt, alt.dataset.spOnce);
      }
    }

    /* Bundle Summary: big figure flips; the sub line shows the alternate. */
    _summary() {
      const bt = document.getElementById('bundleTotal');
      const sub = document.querySelector('[data-sp-summary-sub]');
      if (!bt) return;
      const cur = bt.textContent.replace(/\s+/g, ' ').trim();
      const setSub = (txt) => { if (sub && sub.textContent !== txt) sub.textContent = txt; };
      if (this.mode === 'monthly') {
        if (cur !== bt.dataset.spMo) {
          if (!cur.includes('/mo')) bt.dataset.spOnceHtml = bt.innerHTML;
          const matches = (bt.dataset.spOnceHtml || '').replace(/<[^>]*>/g, ' ').match(/\$[\d,.]+/g);
          const final = matches ? num(matches[matches.length - 1]) : 0;
          if (final) {
            const txt = this._monthlyOf(final);
            bt.dataset.spMo = txt;
            bt.textContent = txt;
            setSub('or ' + money(final) + ' total');
          }
        }
      } else {
        if (bt.dataset.spOnceHtml && cur.includes('/mo')) bt.innerHTML = bt.dataset.spOnceHtml;
        const matches = bt.textContent.match(/\$[\d,.]+/g);
        const final = matches ? num(matches[matches.length - 1]) : 0;
        setSub(final ? 'or ' + this._monthlyOf(final) + ' with Affirm' : '');
      }
    }
  }

  customElements.define('silo-pay-toggle', SiloPayToggle);
})();
